import { useCallback, useEffect, useRef, useState } from 'react';
import Modal from '../shared/Modal';
import Btn from '../shared/Btn';
import Field from '../shared/Field';
import Input from '../shared/Input';
import { makeIdempotencyKey } from '../../../utils/idempotencyKey';
import { checkClaimMdPayerCompatibility, listClaimDocuments } from '../../../api/claimsClient';

const FAX_RE = /^\d{10}$/;

/** Initial (and post-close) Claim.MD compatibility state — nothing checked yet. */
const IDLE_COMPAT = /** @type {const} */ ({ status: 'idle', supported: false, message: '', supportsAttachments: false });

/**
 * @param {{
 *   open: boolean, onClose: () => void,
 *   currentUser: { oa_onboarded?: boolean, claim_md_onboarded?: boolean }|null,
 *   payerId: string|null,
 *   claimId?: string|null,
 *   defaultChannel?: 'office_ally'|'claim_md'|'fax'|'export',
 *   onConfirm: (body: { idempotency_key: string, channel: string, fax_number?: string }) => Promise<any>,
 * }} props
 */
export default function SubmitModal({ open, onClose, currentUser, payerId, claimId = null, defaultChannel = 'office_ally', onConfirm }) {
  const [channel, setChannel] = useState(/** @type {'office_ally'|'claim_md'|'fax'|'export'} */ (defaultChannel));
  const [faxNumber, setFaxNumber] = useState('');
  const [busy, setBusy] = useState(false);
  const [errors, setErrors] = useState(/** @type {any} */ (null));
  // Claim.MD payer-catalog compatibility, resolved lazily the first time the
  // user actually selects the Claim.MD channel.
  const [compat, setCompat] = useState(
    /** @type {{ status: 'idle'|'loading'|'ready'|'error', supported: boolean, message: string, supportsAttachments: boolean }} */
    (IDLE_COMPAT)
  );
  const [attachmentCount, setAttachmentCount] = useState(/** @type {number|null} */ (null));

  const isCustomPayer = payerId === '__custom__';
  const oaOnboarded = !!currentUser?.oa_onboarded;
  const oaDisabled = isCustomPayer || !oaOnboarded;
  const oaTooltip = isCustomPayer
    ? 'Custom payer — no electronic submission available'
    : !oaOnboarded ? 'Office Ally onboarding required' : '';

  // Claim.MD onboarding is per-ORGANIZATION (claims.ClaimMDAccount, not a
  // per-user flag like oa_onboarded) — currentUser.claim_md_onboarded reflects
  // the active organization's account. False covers more than "never
  // onboarded": a standing vendor credential rejection or a blank secret pin
  // also read as not usable, so the tooltip stays neutral about the cause
  // rather than telling an already-onboarded organization to onboard again.
  // Beyond that, the destination payer must exist in the Claim.MD catalog and
  // accept professional (CMS-1500) claims. A failed *lookup* does NOT block
  // submission: this gate exists to catch the mistake early, and Claim.MD
  // itself rejects an unroutable payer downstream, so a reference-data outage
  // must not strand the user at the submit step.
  const claimMdOnboarded = !!currentUser?.claim_md_onboarded;
  const claimMdIncompatible = compat.status === 'ready' && !compat.supported;
  const claimMdDisabled = isCustomPayer || !claimMdOnboarded || !payerId || claimMdIncompatible;
  const claimMdTooltip = isCustomPayer
    ? 'Custom payer — no electronic submission available'
    : !claimMdOnboarded ? 'Claim.MD is not available for this organization'
      : !payerId ? 'No destination payer selected'
        : claimMdIncompatible ? compat.message : '';

  // Only meaningful once we know the payer is reachable but cannot take
  // attachments, and the claim actually carries documents that would be dropped.
  const attachmentWarning =
    compat.status === 'ready' && compat.supported && !compat.supportsAttachments && (attachmentCount ?? 0) > 0
      ? `This payer does not accept electronic attachments — ${attachmentCount} document${attachmentCount === 1 ? '' : 's'} will not be sent.`
      : '';

  // Read the latest hint through a ref so this reset runs when the modal opens,
  // not whenever the parent's channel hint re-renders mid-session.
  const defaultChannelRef = useRef(defaultChannel);
  useEffect(() => { defaultChannelRef.current = defaultChannel; }, [defaultChannel]);

  // Reset per-open so a reopened modal never shows a previous claim's verdict.
  useEffect(() => {
    if (!open) return;
    setChannel(defaultChannelRef.current);
    setErrors(null);
    setCompat(IDLE_COMPAT);
    setAttachmentCount(null);
  }, [open]);

  // Guard against a stale in-flight lookup overwriting fresher state when the
  // user reopens the modal or switches payers mid-request.
  const compatRequestRef = useRef(0);

  const loadCompatibility = useCallback(async () => {
    if (!payerId || isCustomPayer) return;
    const requestId = compatRequestRef.current + 1;
    compatRequestRef.current = requestId;
    setCompat({ status: 'loading', supported: false, message: '', supportsAttachments: false });
    try {
      const result = await checkClaimMdPayerCompatibility(payerId);
      if (compatRequestRef.current !== requestId) return;
      setCompat({
        status: 'ready',
        supported: !!result?.supported,
        message: result?.message || '',
        supportsAttachments: !!result?.supports_attachments,
      });
      // Document count is only needed to size the attachments warning.
      if (result?.supported && !result?.supports_attachments && claimId) {
        const docs = await listClaimDocuments(claimId);
        if (compatRequestRef.current !== requestId) return;
        setAttachmentCount(Array.isArray(docs) ? docs.length : 0);
      }
    } catch (/** @type {any} */ e) {
      if (compatRequestRef.current !== requestId) return;
      setCompat({
        status: 'error',
        supported: false,
        message: 'Could not verify Claim.MD payer support — it will be re-checked on submit.',
        supportsAttachments: false,
      });
    }
  }, [payerId, isCustomPayer, claimId]);

  // Check only when Claim.MD is the selected submission method, per the flow's
  // requirement — no lookup fires for fax/export/Office Ally submissions.
  useEffect(() => {
    if (!open || channel !== 'claim_md') return;
    if (compat.status !== 'idle') return;
    loadCompatibility();
  }, [open, channel, compat.status, loadCompatibility]);

  const submit = async () => {
    if (channel === 'fax' && !FAX_RE.test(faxNumber)) {
      setErrors({ fax_number: '10-digit fax number required' });
      return;
    }
    setBusy(true); setErrors(null);
    try {
      /** @type {{ idempotency_key: string, channel: string, fax_number?: string }} */
      const body = { idempotency_key: makeIdempotencyKey(), channel };
      if (channel === 'fax') body.fax_number = faxNumber;
      await onConfirm(body);
    } catch (/** @type {any} */ e) {
      const status = e?.response?.status;
      const data = e?.response?.data;
      if (status === 422) setErrors({ list: data?.errors || [{ message: data?.detail || 'Validation failed' }] });
      // Two distinct 409 shapes share this status code: a duplicate submission
      // (carries submission_id) and the not-onboarded refusal Part B added
      // (carries {reason: 'claim_md_not_onboarded', detail}, no submission_id).
      // Rendering every 409 as "a submission already exists" showed the
      // not-onboarded case an empty code — the opposite of what it means.
      else if (status === 409 && data?.reason === 'claim_md_not_onboarded') {
        setErrors({ detail: data?.detail || 'Claim.MD is not available for this organization' });
      }
      else if (status === 409) setErrors({ existing: data?.submission_id });
      else setErrors({ detail: data?.detail || e?.message || 'Submit failed' });
    } finally {
      setBusy(false);
    }
  };

  const claimMdBlocked = channel === 'claim_md' && (claimMdDisabled || compat.status === 'loading');

  return (
    <Modal
      open={open} onClose={onClose} title="Submit Claim" width={640}
      footer={<>
        <Btn kind="ghost" onClick={onClose} disabled={busy}>Cancel</Btn>
        <Btn
          kind="primary"
          icon="send"
          onClick={submit}
          disabled={busy || (channel === 'office_ally' && oaDisabled) || claimMdBlocked}
        >
          {busy ? 'Submitting…' : `Submit via ${channel}`}
        </Btn>
      </>}
    >
      {errors?.list && (
        <div style={{ marginBottom: 12, padding: 8, background: 'var(--ab-denied-soft)', color: 'var(--ab-denied)' }}>
          {errors.list.map((/** @type {any} */ e, /** @type {number} */ i) => <div key={i}>{e.message}</div>)}
        </div>
      )}
      {errors?.existing && (
        <div style={{ marginBottom: 12 }}>
          A submission already exists: <code>{errors.existing}</code>
        </div>
      )}
      {errors?.detail && (
        <div style={{ marginBottom: 12, color: 'var(--ab-denied)' }}>{errors.detail}</div>
      )}

      <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
        <ChannelCard
          id="office_ally" label="Office Ally" desc="Electronic submission via clearinghouse"
          selected={channel === 'office_ally'} onSelect={setChannel}
          disabled={oaDisabled} tooltip={oaTooltip}
        />
        <ChannelCard
          id="claim_md" label="Claim.MD" desc="Electronic submission with attachments via clearinghouse"
          selected={channel === 'claim_md'} onSelect={setChannel}
          disabled={claimMdDisabled} tooltip={claimMdTooltip}
        >
          {channel === 'claim_md' && compat.status === 'loading' && (
            <CardNote>Checking Claim.MD payer support…</CardNote>
          )}
          {channel === 'claim_md' && compat.status === 'error' && (
            <CardNote tone="warn">⚠ {compat.message}</CardNote>
          )}
          {channel === 'claim_md' && !!attachmentWarning && (
            <CardNote tone="warn">⚠ {attachmentWarning}</CardNote>
          )}
        </ChannelCard>
        {claimMdIncompatible && (
          <div style={{ marginTop: -6, fontSize: 12, color: 'var(--ab-denied)' }} role="alert">
            {compat.message}
          </div>
        )}
        <ChannelCard
          id="fax" label="Fax" desc="Send via fax"
          selected={channel === 'fax'} onSelect={setChannel}
        >
          {channel === 'fax' && (
            <Field label="Fax number (10 digits)" required error={errors?.fax_number}>
              <Input value={faxNumber} onChange={(/** @type {any} */ e) => setFaxNumber(e.target.value)} maxLength={10} />
            </Field>
          )}
        </ChannelCard>
        <ChannelCard
          id="export" label="Export" desc="Download claim package as PDF"
          selected={channel === 'export'} onSelect={setChannel}
        />
      </div>
    </Modal>
  );
}

/** @param {{ tone?: 'info'|'warn', children: any }} props */
function CardNote({ tone = 'info', children }) {
  return (
    <div style={{
      marginTop: 8,
      fontSize: 12,
      color: tone === 'warn' ? 'var(--ab-warn, var(--ab-denied))' : 'var(--ab-fg-subtle)',
    }}>
      {children}
    </div>
  );
}

/**
 * @param {{
 *   id: 'office_ally'|'claim_md'|'fax'|'export',
 *   label: string, desc: string,
 *   selected: boolean,
 *   onSelect: (id: 'office_ally'|'claim_md'|'fax'|'export') => void,
 *   disabled?: boolean, tooltip?: string,
 *   children?: any,
 * }} props
 */
function ChannelCard({ id, label, desc, selected, onSelect, disabled = false, tooltip = '', children }) {
  return (
    <label
      title={disabled ? tooltip : ''}
      style={{
        display: 'block',
        padding: 12,
        border: `1px solid ${selected ? 'var(--ab-fg)' : 'var(--ab-border-hair)'}`,
        borderRadius: 'var(--ab-radius-md)',
        background: selected ? 'var(--ab-bg-alt)' : 'var(--ab-bg)',
        cursor: disabled ? 'not-allowed' : 'pointer',
        opacity: disabled ? 0.55 : 1,
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
        <input type="radio" checked={selected} onChange={() => !disabled && onSelect(id)} disabled={disabled} />
        <div>
          <div style={{ fontSize: 14, fontWeight: 'var(--ab-weight-semi)' }}>{label}</div>
          <div style={{ fontSize: 12, color: 'var(--ab-fg-subtle)' }}>{desc}</div>
        </div>
      </div>
      {children}
    </label>
  );
}
